import { createContext, useContext, ReactNode } from "react";
import { useVideoCall } from "@/hooks/useVideoCall";
import { VideoCallOverlay } from "./VideoCallOverlay";
import { IncomingCallDialog } from "./IncomingCallDialog";

interface VideoCallContextType {
  startCall: (receiverId: string) => Promise<any>;
  isLoading: boolean;
  isInCall: boolean;
}

const VideoCallContext = createContext<VideoCallContextType | null>(null);

export const useVideoCallContext = () => {
  const context = useContext(VideoCallContext);
  if (!context) {
    throw new Error("useVideoCallContext must be used within VideoCallProvider");
  }
  return context;
};

interface VideoCallProviderProps {
  children: ReactNode;
}

export const VideoCallProvider = ({ children }: VideoCallProviderProps) => {
  const {
    activeCall,
    incomingCall,
    isInCall,
    isLoading,
    startCall,
    acceptCall,
    declineCall,
    endCall,
  } = useVideoCall();

  return (
    <VideoCallContext.Provider value={{ startCall, isLoading, isInCall }}>
      {children}
      
      {/* Incoming call overlay */}
      {incomingCall && (
        <IncomingCallDialog
          callerName={incomingCall.caller_profile?.name || "Unknown"}
          callerAvatar={incomingCall.caller_profile?.avatar_url || undefined}
          onAccept={acceptCall}
          onDecline={(reason) => declineCall(reason)}
        />
      )}
      
      {/* Active call overlay */}
      {isInCall && activeCall && (
        <VideoCallOverlay
          roomUrl={activeCall.room_url}
          partnerName={activeCall.caller_profile?.name || "Partner"}
          partnerAvatar={activeCall.caller_profile?.avatar_url || undefined}
          onEnd={endCall}
          isIncoming={activeCall.receiver_id === activeCall.caller_id}
        />
      )}
    </VideoCallContext.Provider>
  );
};
